import { useState, useEffect } from 'react';
import { Menu, X, ShoppingBag, Globe, User, ChevronDown, LayoutGrid, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../../../assets/logo.png';
import { useAuthStore } from '../../auth/stores/useAuthStore';
import { useCart } from '../../../hooks/useCart';
import { CartDrawer } from '../../cart/components/CartDrawer';
import { useTranslation } from 'react-i18next';

import { NavbarSearch } from './NavbarSearch';
import { useHomepage } from '../../../hooks/useHomepage';

export const Navbar = () => {
    const { t, i18n } = useTranslation('shop');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);

    // Hooks
    const { isAuthenticated } = useAuthStore();
    const { data: cartData } = useCart();
    const cartItemCount = cartData?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

    // Custom Nav Links from CMS (already locale-projected by backend)
    const { data: homepageData } = useHomepage();
    const customNavLinks = (homepageData?.metadata?.navLinks || []).filter(l => l.isActive);

    const toggleLanguage = () => {
        const next = i18n.language === 'ar' ? 'en' : 'ar';
        i18n.changeLanguage(next);
    };

    useEffect(() => {
        const handleOpenCart = () => setIsCartOpen(true);
        window.addEventListener('cart:open', handleOpenCart);
        return () => window.removeEventListener('cart:open', handleOpenCart);
    }, []);

    return (
        <>
            <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-secondary/10 shadow-sm">
                <div className="container mx-auto px-4 py-3 lg:py-0">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:h-24 transition-all duration-300">

                        {/* Top Row: Logo & Mobile Actions */}
                        <div className="flex items-center justify-between">
                            {/* Logo */}
                            <a href="/" className="flex-shrink-0 opacity-100 hover:opacity-80 transition-opacity">
                                <img src={logo} alt="El Raay Optics" className="h-10 lg:h-14 object-contain" />
                            </a>

                            {/* Mobile Actions (Cart & Menu) */}
                            <div className="flex items-center gap-2 lg:hidden">
                                {/* Language Switcher - Mobile */}
                                <button
                                    onClick={toggleLanguage}
                                    className="p-2 text-secondary hover:text-primary hover:bg-secondary/5 rounded-full transition-all"
                                    aria-label="Switch Language"
                                    title={t('nav.languageSwitcher')}
                                >
                                    <Globe size={22} className="stroke-[1.5]" />
                                </button>

                                {/* User Icon - Mobile */}
                                <Link
                                    to="/account"
                                    className="relative p-2 hover:bg-secondary/5 rounded-full transition-colors group"
                                    aria-label="Account"
                                >
                                    <User size={22} className="stroke-[1.5] text-secondary group-hover:text-primary transition-colors" />
                                </Link>


                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="p-2 text-text-main hover:text-primary transition-colors"
                                    aria-label="Menu"
                                >
                                    {isMenuOpen ? <X size={24} className="stroke-[1.5]" /> : <Menu size={24} className="stroke-[1.5]" />}
                                </button>
                            </div>
                        </div>

                        {/* Search Bar & Filter - Desktop (Centered) & Mobile (Full Width below) */}
                        <div className="flex items-center gap-3 flex-1 max-w-2xl lg:mx-12">
                            <NavbarSearch />

                            {/* Cart Icon - Visible only on Mobile beside search */}
                            <button
                                onClick={() => setIsCartOpen(true)}
                                className="lg:hidden relative flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white border border-secondary/10 rounded-xl hover:border-primary/5 hover:bg-secondary/5 hover:text-primary transition-all duration-300 shadow-sm"
                                aria-label="Cart"
                            >
                                <ShoppingBag size={20} className="stroke-[1.5] text-secondary group-hover:text-primary transition-colors" />
                                {cartItemCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[10px] font-bold flex items-center justify-center rounded-full ring-2 ring-white">
                                        {cartItemCount}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Desktop Nav Actions */}
                        <div className="hidden lg:flex items-center gap-8 text-text-main font-medium text-sm tracking-wide">
                            {/* Custom CMS Nav Links */}
                            {customNavLinks.map(link => (
                                <a
                                    key={link.id}
                                    href={link.url}
                                    target={link.openInNewTab ? '_blank' : undefined}
                                    rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                                    className="hover:text-primary transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-px after:bg-primary hover:after:w-full after:transition-all duration-300"
                                >
                                    {link.label}
                                </a>
                            ))}

                            {/* Language Switcher - Desktop */}
                            <button
                                onClick={toggleLanguage}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-secondary hover:text-primary border border-secondary/20 hover:border-primary/40 rounded-full transition-all duration-300 tracking-wider"
                                aria-label="Switch Language"
                            >
                                <Globe size={14} />
                                {t('nav.languageSwitcher')}
                            </button>

                            {/* User Icon - Desktop */}
                            <Link
                                to="/account"
                                className="relative p-2 hover:bg-secondary/5 rounded-full transition-colors group"
                                aria-label="Account"
                            >
                                <User size={22} className="stroke-[1.5] text-secondary group-hover:text-primary transition-colors" />
                            </Link>

                            {/* Cart Icon - Desktop */}
                            <button
                                onClick={() => setIsCartOpen(true)}
                                className="relative p-2 hover:bg-secondary/5 rounded-full transition-colors group"
                            >
                                <ShoppingBag size={22} className="stroke-[1.5] text-secondary group-hover:text-primary transition-colors" />
                                {cartItemCount > 0 && (
                                    <span className="absolute top-0 right-0 w-4 h-4 bg-primary text-white text-[10px] font-bold flex items-center justify-center rounded-full ring-2 ring-white">
                                        {cartItemCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu Dropdown */}
                <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out bg-background/95 backdrop-blur-xl border-t border-secondary/5 ${isMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="container mx-auto px-6 py-8 flex flex-col gap-6">
                        <Link onClick={() => setIsMenuOpen(false)} to="/account" className="text-xl font-medium text-text-main hover:text-primary transition-colors flex items-center justify-between group border-b border-secondary/5 pb-4">
                            {isAuthenticated ? t('nav.myAccount', 'My Account') : t('nav.login', 'Login / Register')} <span className="opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all text-primary">&rarr;</span>
                        </Link>

                        {/* Custom CMS Nav Links - Mobile */}
                        {customNavLinks.map(link => (
                            <a
                                key={link.id}
                                href={link.url}
                                target={link.openInNewTab ? '_blank' : undefined}
                                rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center justify-between py-4 text-xl font-medium text-text-main border-b border-gray-100 group hover:text-primary transition-colors"
                            >
                                {link.label} <span className="opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all text-primary">&rarr;</span>
                            </a>
                        ))}
                    </div>
                </div>
            </nav >
            <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
        </>
    );
};
